/**
 * EventRibbonDot component.
 *
 * A single dot on the event ribbon, positioned by timestamp and colored by outcome.
 * Wraps a <button> in a Radix HoverCard for peek panel.
 * Click navigates to /event/{id} via react-router-dom useNavigate.
 *
 * Position calculation:
 *   left% = ((ts - t_start) / (t_end - t_start)) * 100
 * This maps the oldest alert to the left edge and newest to the right edge.
 *
 * Dot radius scales with severity per design spec section 3.4.
 *
 * Design spec reference: section 3.4
 */
import { useNavigate } from 'react-router-dom';
import * as HoverCard from '@radix-ui/react-hover-card';
import { tokenValues } from '@/design/tokens';
import type { AlertSummary, AlertOutcome } from './schemas';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Maps alert outcome to the ribbon dot color. */
function getOutcomeColor(outcome: AlertOutcome | null): string {
  switch (outcome) {
    case 'turned_away':
      return tokenValues.colors.status.ok;
    case 'entered':
      return tokenValues.colors.status.alert;
    case 'loitering':
      return tokenValues.colors.status.warn;
    default:
      return tokenValues.colors.text.tertiary;
  }
}

/** Maps severity to dot radius in pixels. */
function getDotRadius(severity: AlertSummary['severity']): number {
  switch (severity) {
    case 'critical':
      return 8;
    case 'warning':
      return 6;
    default:
      return 4;
  }
}

/** Formats a UTC timestamp as a local time string (HH:MM). */
function formatTime(ts: string): string {
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/** Human-readable outcome label for peek panel. */
function outcomeLabel(outcome: AlertOutcome | null): string {
  switch (outcome) {
    case 'turned_away':
      return 'Turned away';
    case 'entered':
      return 'Entered';
    case 'loitering':
      return 'Loitering';
    default:
      return 'Unknown';
  }
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Props {
  alert: AlertSummary;
  /** Left position as a percentage of the ribbon width (0-100). */
  leftPct: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * EventRibbonDot: a single positioned dot on the event ribbon.
 *
 * @param alert - The alert data for this dot
 * @param leftPct - Horizontal position as percentage of ribbon width
 */
export function EventRibbonDot({ alert, leftPct }: Props) {
  const navigate = useNavigate();
  const color = getOutcomeColor(alert.outcome);
  const radius = getDotRadius(alert.severity);

  function handleClick() {
    navigate(`/event/${alert.id}`);
  }

  return (
    <HoverCard.Root openDelay={200} closeDelay={100}>
      <HoverCard.Trigger asChild>
        <button
          onClick={handleClick}
          data-testid={`ribbon-dot-${alert.id}`}
          data-outcome={alert.outcome ?? 'other'}
          aria-label={`Alert at ${formatTime(alert.ts)}: ${outcomeLabel(alert.outcome)} - ${alert.zone_name ?? 'Unknown zone'}`}
          style={{
            position: 'absolute',
            left: `${leftPct}%`,
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: `${radius * 2}px`,
            height: `${radius * 2}px`,
            borderRadius: '50%',
            background: color,
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            zIndex: 2,
            transition: 'transform 140ms ease, opacity 140ms ease',
          }}
        />
      </HoverCard.Trigger>

      <HoverCard.Portal>
        <HoverCard.Content
          side="top"
          sideOffset={8}
          style={{
            background: tokenValues.colors.bg.surface2,
            border: `1px solid ${tokenValues.colors.border.subtle}`,
            borderRadius: tokenValues.radii.md,
            padding: '10px 14px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
            minWidth: '180px',
            zIndex: 50,
          }}
        >
          {/* Timestamp + zone */}
          <div
            style={{
              fontFamily: tokenValues.fonts.mono,
              fontSize: '12px',
              color: tokenValues.colors.text.secondary,
              marginBottom: '4px',
            }}
          >
            {formatTime(alert.ts)}
            {alert.zone_name ? ` - ${alert.zone_name}` : ''}
          </div>

          {/* Outcome */}
          <div
            style={{
              fontFamily: tokenValues.fonts.sans,
              fontSize: '13px',
              color,
              fontWeight: '500',
            }}
          >
            {outcomeLabel(alert.outcome)}
          </div>

          {/* Camera thumbnail if available */}
          {alert.has_camera_clip && alert.thumbnail_url && (
            <img
              src={alert.thumbnail_url}
              alt="Event thumbnail"
              style={{
                width: '120px',
                height: '68px',
                objectFit: 'cover',
                borderRadius: tokenValues.radii.sm,
                marginTop: '8px',
              }}
            />
          )}

          <HoverCard.Arrow style={{ fill: tokenValues.colors.border.subtle }} />
        </HoverCard.Content>
      </HoverCard.Portal>
    </HoverCard.Root>
  );
}
